import { BookOpen } from "lucide-react";
import { useState } from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import type { RegistryEpisode, UserRole } from "../../registry";
import { AudioBookPhase } from "../AudioBookPhase";
import EpisodeToolbar from "./EpisodeToolbar";
import { GenrePattern } from "./GenrePattern";

interface MageBookProps {
  chapter: RegistryEpisode;
  genre?: string;
  userRole?: UserRole;
}

function EpisodeNumber({ episode }: { episode: RegistryEpisode }) {
  if (episode.number == null) {
    return null;
  }

  return (
    <span className="shrink-0 m-0 rounded-sm bg-muted px-1.5 py-0.5 text-xs tabular-nums font-medium uppercase tracking-wide text-muted-foreground">
      {episode.isPilot ? "Pilot" : episode.number}
    </span>
  );
}

export function EpisodeCard({ chapter, genre, userRole }: MageBookProps) {
  // State rather than an imperative classList mutation, which React clobbers.
  const [coverFailed, setCoverFailed] = useState(false);
  const showCover = Boolean(chapter.cover) && !coverFailed;

  return (
    <Card className="w-64 shrink-0 overflow-hidden flex flex-col h-full p-0">
      {showCover && (
        <img
          src={chapter.cover}
          alt=""
          className="h-36 w-full object-cover"
          onError={() => setCoverFailed(true)}
        />
      )}
      <div
        className="h-36 w-full bg-muted flex items-center justify-center relative overflow-hidden"
        hidden={showCover}
        aria-hidden="true"
      >
        <GenrePattern genre={genre} />
        <BookOpen className="h-8 w-8 text-muted-foreground/40 relative" />
      </div>
      <CardContent className="flex-1 relative">
        <div className="absolute right-0.5 -top-5.5">
          <AudioBookPhase phase={chapter.phase} />
        </div>
        <div className="absolute left-1 -top-6">
          <EpisodeNumber episode={chapter} />
        </div>
        <div className="flex items-start gap-1.5">
          <h3 className="text-sm font-medium leading-tight line-clamp-2">
            {chapter.label}
          </h3>
        </div>
        {chapter.summary && (
          <p className="text-xs text-muted-foreground mt-1.5 line-clamp-3">
            {chapter.summary}
          </p>
        )}
      </CardContent>
      <CardFooter className="p-3 pt-0">
        <EpisodeToolbar
          chapter={chapter}
          className="w-full"
          userRole={userRole}
        />
      </CardFooter>
    </Card>
  );
}
